/**********************************************************************************************************
 *   BASE IMPORTS
 **********************************************************************************************************/
import { useNavigate } from 'react-router-dom'
import {
    ChartSquareBarIcon,
    CreditCardIcon,
    /* DocumentIcon, */
    IdentificationIcon,
    KeyIcon,
    /* ServerIcon,
    ShoppingBagIcon, */
    UserIcon,
    UsersIcon
} from '@heroicons/react/solid'

/**********************************************************************************************************
 *   SHARED IMPORTS
 **********************************************************************************************************/
import { Box, /* Bubble, */ Button, ButtonGroup, FrameInset, Theme } from '@nexigendigital/nxui'
import { useAppSelector } from 'store/hooks'
import { Weight } from 'models/enums'
import { getInitials } from 'helpers/utils'

import Invoices from 'containers/billing/invoices'

/**********************************************************************************************************
 *   STYLE
 **********************************************************************************************************/
import styled from 'styled-components'

const Profile = {
    Box: styled(Box)`
        width: 100%;
    `,
    Avatar: styled.div`
        display: none;
        align-items: center;
        justify-content: center;
        height: 80px;
        width: 80px;
        border-radius: 9999px;
        color: ${props => (props.theme['white--100'])};
        background-color: ${Theme.Functions.randomColor()};
        font-size: 2rem;
        ${Theme.Functions.setFont('Inter', Weight.ExtraBold)};

        ${props => (Theme.Functions.mediaBreakUp(props.theme['breakpoints-sm']))} {
            display: flex;
        }
    `,
    Icon: styled(UsersIcon)`
        position: absolute;
        top: 20px;
        right: 20px;
        color: ${props => (props.theme['icon--100'])};
    `,
    Title: styled.div`
        display: flex;
        width: 100%;
        padding: 20px 25px;

        > div:first-of-type {
            margin-right: 20px;
        }

        > div:last-of-type {
            display: flex;
            align-items: flex-start;
            justify-content: center;
            flex-direction: column;

            > span {
                font-size: 32px;
                color: ${props => (props.theme['text--300'])};
                ${Theme.Functions.setFont('Inter', Weight.ExtraBold)};

                ${props => (Theme.Functions.mediaBreakDown(props.theme['breakpoints-sm']))} {
                    font-size: 22px;
                }
            }

            > h1 {
                font-size: 42px;
                color: ${props => (props.theme['primary--100'])};
                line-height: 1;

                ${props => (Theme.Functions.mediaBreakDown(props.theme['breakpoints-sm']))} {
                    font-size: 28px;
                }
            }
        }
    `,
    Actions: styled.div`
        width: 100%;
        height: 120px;
        padding: 20px;
        background-color: ${props => (props.theme['background--100'])};
        border-radius: 0 0 16px 16px;

        ${props => (Theme.Functions.mediaBreakDown(props.theme['breakpoints-sm']))} {
            height: fit-content;
            padding: 15px;
        }

        > div:first-of-type {
            margin-bottom: 15px;
            width: fit-content;

            > span {
                margin-right: 25px;
            }
        }

        > div {
            display: flex;
            justify-content: space-between;

            ${props => (Theme.Functions.mediaBreakDown(props.theme['breakpoints-sm']))} {
                flex-direction: column;
            }

            > span {
                display: flex;
                font-size: 14px;
                color: ${props => (props.theme['text--100'])};
                ${Theme.Functions.setFont('Inter', Weight.Medium)};

                > svg {
                    margin-right: 5px;

                    ${props => (Theme.Functions.mediaBreakDown(props.theme['breakpoints-sm']))} {
                        margin-right: 10px;
                    }
                }
            }

            button {
                margin: 0.2rem 0;
            }
        }
    `,
    Group: styled(ButtonGroup)`
        ${props => (Theme.Functions.mediaBreakDown(props.theme['breakpoints-sm']))} {
            button {
                width: 100%
            }
        }
    `,
    Shop: styled(Button)`
        ${props => (Theme.Functions.mediaBreakDown(props.theme['breakpoints-sm']))} {
            width: 100%;
        }
    `
}

/* const Bubbles = styled.div`
    display: flex;
    width: 100%;

    ${props => (Theme.Functions.mediaBreakDown(props.theme['breakpoints-2xl']))} {
        flex-wrap: wrap;
    }

    ${props => (Theme.Functions.mediaBreakDown(props.theme['breakpoints-xl']))} {
        flex-wrap: nowrap;
    }

    ${props => (Theme.Functions.mediaBreakDown(props.theme['breakpoints-md']))} {
        width: 100%;
    }

    ${props => (Theme.Functions.mediaBreakDown(props.theme['breakpoints-sm']))} {
        width: 100%;
        flex-wrap: wrap;
    }

    > div {
        margin: 0 20px 20px 0;
        min-width: auto;

        ${props => (Theme.Functions.mediaBreakDown(props.theme['breakpoints-2xl']))} {
            margin: 0 0 20px 0;
        }

        ${props => (Theme.Functions.mediaBreakDown(props.theme['breakpoints-xl']))} {
            margin: 0 20px 20px 0;
        }

        ${props => (Theme.Functions.mediaBreakDown(props.theme['breakpoints-sm']))} {
            margin: 0 0 20px 0;
        }

        &:last-of-type {
            margin: 0 0 20px 0;
        }
    }
` */

/**********************************************************************************************************
 *   COMPONENT START
 **********************************************************************************************************/
export default function Dashboard() {
    const navigate = useNavigate()
    const { appSession: { user, account } } = useAppSelector((state) => state.app)
    const { firstname, lastname } = user!
    const { account_number, role } = account!

    return (
        <FrameInset
            inset={'split'}
            location={{ pathname: '/Dashboard' }}
            icon={<ChartSquareBarIcon />}
            left={{
                render: <Profile.Box>
                    <Profile.Icon width={'20px'} />
                    <Profile.Title>
                        <Profile.Avatar>{getInitials(`${firstname} ${lastname}`)}</Profile.Avatar>
                        <div>
                            <span>Welcome back,</span>
                            <h1>{firstname}</h1>
                        </div>
                    </Profile.Title>
                    <Profile.Actions>
                        <div>
                            <span>
                                <IdentificationIcon width={'20px'} />
                                {account_number}
                            </span>
                            <span>
                                <KeyIcon width={'20px'} />
                                {role?.name}
                            </span>
                        </div>
                        <div>
                            <Profile.Group
                                color={'secondary'}
                                list={[
                                    {
                                        type: 'button',
                                        icon: <UserIcon />,
                                        label: 'Account',
                                        onClick: () => navigate('/account')
                                    },
                                    {
                                        type: 'button',
                                        icon: <CreditCardIcon />,
                                        label: 'Billing',
                                        onClick: () => navigate('/billing')
                                    }/* ,
                                    {
                                        type: 'button',
                                        icon: <ServerIcon />,
                                        label: 'Services',
                                        onClick: () => navigate('/services')
                                    } */
                                ]}
                            />
                            {/* <Profile.Shop color={'primary'} icon={<ShoppingBagIcon />} onClick={() => navigate('/shop')}>Shop</Profile.Shop> */}
                        </div>
                    </Profile.Actions>
                </Profile.Box>
            }}
            right={{
                render: <Invoices /> /* <Bubbles>
                    <Bubble title={'Active Services'} desc={'27'} icon={<ServerIcon />} action={{ label: 'View All', onClick: () => navigate('/services') }} />
                    <Bubble title={'Active Domains'} desc={'5'} icon={<GlobeAltIcon />} action={{ label: 'View All', onClick: () => navigate('/domains') }} />
                    <Bubble title={'Outstanding Invoices'} desc={'1'} icon={<DocumentIcon />} action={{ label: 'View All', onClick: () => navigate('/billing') }} />
                </Bubbles> */
            }}
        />
    )
}
